"use client";

import React from "react";
import { ArrowRight, Compass, CheckCircle2, Globe, FileSearch, Layers } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
export default function InternationalStrategy() {
  return <section id="internationalseostrategy" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border/60">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <Badge variant="outline" className="mb-4 px-3 py-1 text-xs border-primary/20 text-primary bg-primary/5 rounded-lg font-medium inline-flex items-center gap-1.5">
            <Globe className="w-3.5 h-3.5 text-primary" />
            <span>Market Selection &amp; Strategic Feasibility</span>
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-foreground font-default mb-4">
            International SEO Strategy and Market Research
          </h2>
          <p className="text-muted-foreground text-base md:text-lg font-sans leading-relaxed">
            Entering new countries without thorough market research leads to wasted development time, poorly targeted content, and low conversion rates. I help you evaluate cross-border search demand, operational capacity, and local competition before committing resources.
          </p>
        </div>

        {/* 3 Strategic Pillars (H3 Subsections) */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mb-12">
          <Card className="border border-border bg-card text-card-foreground shadow-xs flex flex-col justify-between">
            <CardHeader className="pb-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0 mb-3 text-primary">
                <FileSearch className="w-5 h-5" />
              </div>
              <CardTitle className="text-lg font-semibold font-default text-foreground">
                Assessing Existing International Demand
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm text-muted-foreground leading-relaxed">
              <p>
                Many businesses already attract organic visitors from other countries without realising it. I review your analytics and Search Console data to identify existing impressions, queries, and landing pages that foreign users engage with.
              </p>
              <p>
                Starting with empirical demand provides a data-led basis for selecting your initial target countries rather than relying on guesswork.
              </p>
            </CardContent>
          </Card>

          <Card className="border border-border bg-card text-card-foreground shadow-xs flex flex-col justify-between">
            <CardHeader className="pb-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0 mb-3 text-primary">
                <Compass className="w-5 h-5" />
              </div>
              <CardTitle className="text-lg font-semibold font-default text-foreground">
                Prioritising Markets &amp; Operational Fit
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm text-muted-foreground leading-relaxed">
              <p>
                Search demand alone does not guarantee commercial success. I cross-reference keyword volumes against operational feasibility: shipping costs, local regulations, payment methods, and customer service readiness.
              </p>
              <p>
                I advise honestly when a market is not worth entering, protecting your team from entering territories with prohibitive barriers or hyper-competitive domestic players.
              </p>
            </CardContent>
          </Card>

          <Card className="border border-border bg-card text-card-foreground shadow-xs flex flex-col justify-between">
            <CardHeader className="pb-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0 mb-3 text-primary">
                <Layers className="w-5 h-5" />
              </div>
              <CardTitle className="text-lg font-semibold font-default text-foreground">
                Phased Rollout vs All-at-Once Expansion
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm text-muted-foreground leading-relaxed">
              <p>
                Attempting to launch in ten countries simultaneously divides focus and dilutes authority. I recommend a disciplined, phased approach: launch in one to three priority markets first.
              </p>
              <p>
                We refine technical hreflang signals, translation quality, and local conversion rates in that first wave, establishing a proven blueprint before rolling out to additional territories.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Audit First Callout */}
        <div className="bg-muted/50 border border-border rounded-2xl p-6 sm:p-8 md:p-10 shadow-xs flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="max-w-3xl space-y-2">
            <h3 className="text-xl sm:text-2xl font-semibold text-foreground font-default">
              Start With an International SEO Audit
            </h3>
            <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
              Before redesigning site structures or commissioning expensive translations, an upfront{" "}
              <Link to="/SeoAuditsPage" className="text-primary font-medium underline underline-offset-4 hover:underline">
                SEO audit
              </Link>{" "}
              uncovers current indexation issues, canonical conflicts, and high-potential queries in your overseas traffic. For complex multi-regional builds, aligning with deep{" "}
              <Link to="/TechnicalSeoPage" className="text-primary font-medium underline underline-offset-4 hover:underline">
                Technical SEO
              </Link>{" "}
              ensures search engines crawl and index alternate language versions cleanly.
            </p>
          </div>
          <div className="shrink-0 flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <Button asChild size="lg" className="shadow-xs">
              <Link to="/ContactPage" className="inline-flex items-center gap-2">
                <span>Book a Free Consultation</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="shadow-xs">
              <Link to="/SeoAuditsPage">
                <span>Start with an Audit</span>
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}